import { useEffect, useState } from "react";
import type { Size } from "../lib/selection-geometry";

/**
 * The intrinsic pixel dimensions of an image URL, or null until it loads.
 *
 * Both the stage (to decide cover vs contain) and the selection math (to map a
 * drawn rect back to source pixels) need this. Probing twice is free — the
 * second `new Image()` for the same src resolves from the browser cache — so
 * each caller owns its own probe rather than threading the value between them.
 */
export function useNaturalImageSize(url: string | undefined): Size | null {
  const [size, setSize] = useState<Size | null>(null);

  useEffect(() => {
    if (!url) {
      setSize(null);
      return;
    }
    let cancelled = false;
    setSize(null);
    const probe = new Image();
    probe.onload = () => {
      if (!cancelled) {
        setSize({ width: probe.naturalWidth, height: probe.naturalHeight });
      }
    };
    probe.src = url;
    return () => {
      cancelled = true;
    };
  }, [url]);

  return size;
}
